import { type PropsWithChildren, useMemo } from 'react'

import { fvDateUtc } from '@fv/dates'
import type { SavedView } from '@fv/models'

import { readyCompletedStatusOptions } from '@/components/CustomFilterForm/filterOptions'
import type { FilterConfig } from '@/components/CustomFilterForm/types'
import type { SavedFilter, SavedFilterGroup } from '@/types/SavedFilter'

import { useAccountFeatures, useRole } from '../auth/hooks'
import {
  activeFilter,
  allConfirmedFilter,
  archivedConfirmedFilter,
  confirmedFilter,
  deliveredFilter,
  pickedUpFilter,
} from './confirmed-filters'
import { getFilters } from './filterRegistry'
import { ShipmentFilterListProvider } from './ShipmentFilterListProvider'
import { vendorCreatedFilter } from './shared-filters'
import { useHiddenViews, useSavedViews } from './useSavedViews'

export const ConfirmedFiltersProvider = ({ children }: PropsWithChildren) => {
  const filters = useConfirmedFilters()
  const filterConfig = useConfirmedFilterConfig()
  return (
    <ShipmentFilterListProvider
      state={{
        listType: 'readyCompletedList',
        filters,
        filterConfig,
      }}
    >
      {children}
    </ShipmentFilterListProvider>
  )
}

export type ConfirmedFilter = SavedFilter & Pick<SavedView, 'scheduledReports'>
export const useConfirmedFilters = (): ConfirmedFilter[] => {
  const { vendorPortal } = useAccountFeatures()
  const role = useRole()
  const confirmedFilters = useMemo<
    Array<SavedFilter> | Array<SavedFilterGroup>
  >(
    () => [
      activeFilter,
      ...(vendorPortal && role === 'shipper' ? [vendorCreatedFilter()] : []),
      confirmedFilter,
      pickedUpFilter,
      deliveredFilter,
      allConfirmedFilter,
      archivedConfirmedFilter,
    ],
    [vendorPortal, role],
  )

  const hiddenViews = useHiddenViews()
  const savedViews = useSavedViews('readyCompletedList')

  return useMemo(() => {
    const systemFilters =
      confirmedFilters?.filter(f => !hiddenViews.find(id => id === f._id)) ?? []

    const splitIndex = systemFilters.findIndex(f => f._id === 'all')

    return systemFilters
      .slice(0, splitIndex)
      .concat(savedViews)
      .concat(systemFilters.slice(splitIndex))
      .map(s => ({
        ...s,
        scheduledReports: s.scheduledReports?.map(r => ({
          ...r,
          time: fvDateUtc(r.time, 'HH:mm').local().format('HH:mm'),
        })),
      }))
  }, [hiddenViews, savedViews, confirmedFilters])
}

const useConfirmedFilterConfig = (): FilterConfig[] => {
  const role = useRole()
  const { vendorPortal } = useAccountFeatures()

  return getFilters([
    'originAccount[]',
    'destAccount[]',
    'pickupDate',
    'bookedDate',
    'deliveryDate',
    'createdDate',
    {
      label: 'Status',
      name: 'status[]',
      options: readyCompletedStatusOptions,
    },
    'mode[]',
    'equipmentType[]',
    'carrier[]',
    ...(role === 'shipper'
      ? ([
          'direction[]',
          'quotedBy[]',
          'bookedBy[]',
          'isInterline',
          'isFreightCollect',
          'pricingMethod[]',
          'pricingType[]',
          'tag[]',
          'missingTag[]',
          'glCode[]',
          'location[]',
          'hasDocs[]',
          'missingDocs[]',
          'upload[]',
          'isArchived',
          ...(vendorPortal ? getFilters(['isVendor', 'vendors[]']) : []),
        ] as const)
      : []),
  ])
}
